/**
 * 生成默认画板名称
 * 格式：未命名画板ddHHMM
 * @returns 格式化的默认画板名称
 */
export const generateDefaultCanvasName = (): string => {
  const now = new Date();
  const day = now.getDate().toString().padStart(2, "0");
  const hour = now.getHours().toString().padStart(2, "0");
  const minute = now.getMinutes().toString().padStart(2, "0");
  return `未命名${day}${hour}${minute}`;
};

/**
 * 占位符类型枚举
 */
export type PlaceholderType = "generation" | "upload" | "psd";

/**
 * 占位符状态枚举
 */
export type PlaceholderStatus = "loading" | "failed" | "review_failed";

/**
 * 画布对象自定义属性接口定义
 * 用于扩展 Fabric.js 对象属性，实现业务逻辑
 *
 * 字段设计原则：
 * 1. id - 元素唯一标识（fabric生成），用于图层列表、选中状态等
 * 2. clientId - 生图任务ID（可选），仅在画布内发起生图时存在
 * 3. resultId - 生图结果ID（可选），用于图库定位功能
 * 4. 占位符相关 - 用于标识和管理临时占位图层
 * 5. originalSrc - 原始图片地址，用于高清下载/重绘/图生图
 */
export interface CanvasObjectProperties {
  // --- 核心标识字段 ---
  /**
   * 元素唯一标识
   * 由 fabric.js 生成（或使用 nanoid），在整个画布生命周期内唯一
   * 用于：图层列表展示、选中状态管理、元素查找等
   * 复制元素时会生成新的 id
   */
  id: string;

  /**
   * 图层名称
   * 用于 UI 展示
   */
  name: string;

  // --- 生图任务相关 ---
  /**
   * 生图任务客户端ID
   * 仅在画布内发起生图时存在，用于关联生图任务状态
   * 本地上传、图库图片不具有此字段
   */
  clientId?: string;

  /**
   * 生图结果ID
   * 用于图库中定位到画布中的对应元素
   * 本地上传图片和占位符不具有此字段
   */
  resultId?: string;

  /**
   * 作品版本 ID（与 chat result.versionId / VersionRecord.id 一致，geneDownload.artworkVersionId）
   */
  versionId?: number;

  /**
   * 资产类型
   * - image: 普通图片资源
   * - psd: PSD 资源（用于展示 PSD 专属卡片）
   */
  assetType?: "image" | "psd";

  // --- 序列帧卡片相关（ComfyUI picsType=4/5 序列帧产物）---
  /** 是否为序列帧压缩包卡片图层（合成控件，操作入口在卡片 overlay） */
  isFrameSequence?: boolean;
  /** 序列帧帧数（卡片角标展示） */
  frameCount?: number;
  /** 后端下发的序列帧压缩包 URL（直下优先，缺失时前端 jszip 打包） */
  frameZipUrl?: string;
  /** 序列帧帧图 URL 列表（前端 jszip 打包用） */
  frameUrls?: string[];
  /** 源生成记录 clientId：合成图层的 clientId 是派生值（如 `${clientId}-frames`），查看详情按源记录查询 */
  sourceClientId?: string;

  // --- 占位符相关 ---
  /**
   * 是否为占位符图层
   * true 表示这是一个临时占位图层（生图中或上传中）
   */
  isPlaceholder?: boolean;

  /**
   * 占位符类型
   * - generation: 生图任务占位符
   * - upload: 本地上传占位符
   * - psd: PSD 资产占位符（用于画布覆盖层交互与展示）
   */
  placeholderType?: PlaceholderType;

  /**
   * 占位符状态
   * - loading: 加载/生成中
   * - failed: 失败
   */
  placeholderStatus?: PlaceholderStatus;

  /**
   * 是否为提示词增强阶段占位符
   * 用于刷新恢复时区分“真实生图占位符”和“仅增强态占位符”
   */
  isEnhancingPromptPlaceholder?: boolean;

  // --- 图片相关 ---
  /**
   * 原始图片地址
   * 用于高清下载、重绘、图生图等功能
   * 保存原始输入的URL，可能与 src 不同（如 src 被转为 base64）
   */
  originalSrc?: string;

  /**
   * 原始宽度（上传/创建时尺寸）
   * 用于信息标签展示、高清下载、图生图等场景
   * 备注：老数据可能缺失，需迁移补充
   */
  originalWidth?: number;

  /**
   * 原始高度（上传/创建时尺寸）
   * 用于信息标签展示、高清下载、图生图等场景
   * 备注：老数据可能缺失，需迁移补充
   */
  originalHeight?: number;

  // --- 线条相关 ---
  /**
   * 线条模式：line=线条，arrow=箭头
   * 自定义属性，需显式序列化，否则刷新后丢失退化为普通线条
   */
  lineMode?: "line" | "arrow";

  /**
   * 线条端点样式：none=无，triangle=三角，dot=圆点
   * 自定义属性，需显式序列化，否则刷新后端点（箭头）消失
   */
  lineEndpoint?: "none" | "triangle" | "dot";

  // --- 铅笔笔触相关 ---
  /**
   * 是否为「尚未转图」的铅笔矢量笔触组
   * true 表示该 group 由画笔工具绘制、仍是矢量形态，最终应被转成 PNG 图片图层替换。
   * 正常流程下停笔达阈值即转图并移除该 group；但若在转图完成前刷新/关闭，矢量 group 会被持久化。
   * 需显式序列化：刷新恢复后据此扫描残留矢量笔触并补一次转图（见 usePencilGroupUpload），
   * 使画板数据最终收敛为图片图层。转图成功后 group 被移除，替换出的 image 不带此标记。
   */
  isPencilStroke?: boolean;

  // --- 全能拆分图组标记 ---
  /**
   * 是否为全能拆分产物图组（fabric.Group）
   * 与 _splitTaskId 搭配，用于选中图组时显示「查看拆分详情」按钮与「已拆分」标签。
   * 需显式序列化：否则刷新后图组丢失标记，工具栏入口消失。
   */
  _isSplitGroup?: boolean;

  /**
   * 拆分任务 ID（图组落地时生成，等价于服务端会话 clientId 关联键）
   * 图组与其子对象都会携带；刷新后据此从画布定位图组打开详情弹窗。
   */
  _splitTaskId?: string;

  /**
   * 拆分图层文件标识（子对象唯一）
   * 详情弹窗删除/修补/排序回填画布时按此字段匹配对应子对象。
   */
  _splitLayerFile?: string;

  /**
   * 拆分图层名称（子对象展示名）
   * 详情弹窗改名后回填画布时同步。
   */
  _splitLayerName?: string;

  /**
   * 拆分图层服务端稳定 layerId（子对象）
   * 修补/回滚请求必须使用该值；刷新后从画布重建详情图层时据此恢复。
   */
  _splitLayerId?: string;

  /**
   * 拆分会话 clientId（图组）
   * 图组落地时写入；刷新后从画布重建详情时据此恢复拆分会话，修补/回滚/继续拆分可用。
   */
  _splitClientId?: string;

  /**
   * 拆分会话 resultRevision（图组，CAS 版本）
   * 与 _splitClientId 配套持久化，修补/回滚提交时作为 sourceRevision。
   */
  _splitResultRevision?: number;

  /**
   * 拆图结果最新 manifest URL（图组）
   * 刷新后不依赖 RESUME 兜底即可定位最新清单。
   */
  _splitResultManifestUrl?: string;

  /**
   * 拆分会话所属作品 id（图组）
   * 刷新后图组归属校验（跨画布复制/切换防护）。
   */
  _splitArtworkId?: number;

  /**
   * 拆分副本的源会话 clientId（图组，画廊「复制/定位」落地时写入）
   * 画廊定位按此判重已落地副本；不序列化则刷新后 tag 丢失、再点必重复复制一份。
   */
  _splitSourceClientId?: string;

  /**
   * 拆分副本的源版本 id（图组，与 _splitSourceClientId 配套写入）
   * 源记录缺 versionId 时由 clientId 反查解析，定位判重的第二身份。
   */
  _splitSourceVersionId?: number;

  /**
   * 修补历史版本（图组子对象，结构见 SplitLayer._repairVers）
   * 刷新后重建详情仍可「回滚修补」；本层仅序列化透传，不做结构解析。
   */
  _splitRepairVers?: unknown[];

  /**
   * 是否为全能拆分扫描态临时对象（副本/条纹遮罩/扫描线）
   * 扫描动效是内存态，但画布保存（toJSON）会将其持久化；
   * 刷新后残留为不可交互的冻结副本，需显式序列化该标签，断点续传时据此清理。
   */
  _isSplitScanObject?: boolean;

  // --- 扩展数据 ---
  /** 非 image 类型的业务扩展数据（JSON 字符串） */
  extraData?: string;
  /**
   * PSD 文件名（优先展示）
   * 例如：角色拆分_01.psd
   */
  psdFileName?: string;

  /**
   * PSD 文件内部图层数
   * 注意：该值表示单个 PSD 内的图层数量，与当前画布对象数量无关
   */
  psdLayerCount?: number;

  /**
   * PSD 下载地址（原始直链）
   */
  psdDownloadUrl?: string;

  /**
   * 模板名称（用于文件名回退展示）
   */
  templateName?: string;

  /**
   * 模板序号（用于文件名回退展示）
   * 序号应来自结果数据，不应由前端按画布对象计数推导
   */
  templateSerial?: number | string;

  // --- 布局流相关 ---
  /**
   * 是否参与自动布局流
   * true = 系统自动排列的对象（占位符、替换后的结果图）
   * false = 用户手动拖动后脱离自动布局
   * 仅流内对象会在删除/替换时参与自动位移重排
   */
  isInFlow?: boolean;
  /**
   * 是否为当前参考点对象
   * true 表示后续新增占位符将以该对象作为参考
   */
  isReferencePoint?: boolean;
  /** 是否为当前底部生成游标节点（参与撤销/重做快照恢复） */
  isBottomGenerateCursor?: boolean;
  /** 是否为图片上下文工具生成的后处理结果（历史画布兜底锚点需排除） */
  isPostProcessResult?: boolean;
  /** 插入顺序独立于 Fabric 显示层级，刷新后点击置顶不改变生成锚点。 */
  canvasInsertionOrder?: number;

  // --- 基础状态字段 ---
  /** 锁定状态 */
  locked: boolean;
  /** 可见性 */
  visible: boolean;
  /** 是否可选中 */
  selectable: boolean;
  /** 是否响应事件 */
  evented: boolean;
}

/**
 * 画布对象序列化字段列表
 * 用于 toJSON/toObject 时保留的自定义属性
 * 注意：必须与 CanvasObjectProperties 接口保持一致
 */
export const CANVAS_OBJECT_FIELDS: (keyof CanvasObjectProperties)[] = [
  // --- 核心标识 ---
  "id",
  "name",

  // --- 生图任务相关 ---
  "clientId",
  "resultId",
  "versionId",
  "assetType",

  // --- 占位符相关 ---
  "isPlaceholder",
  "placeholderType",
  "placeholderStatus",
  "isEnhancingPromptPlaceholder",

  // --- 图片相关 ---
  "originalSrc",
  "originalWidth",
  "originalHeight",
  "psdFileName",
  "psdLayerCount",
  "psdDownloadUrl",
  "templateName",
  "templateSerial",

  // --- 序列帧卡片相关（ComfyUI picsType=4 视频+序列帧）---
  "isFrameSequence",
  "frameCount",
  "frameZipUrl",
  "frameUrls",
  "sourceClientId",

  // --- 线条相关 ---
  "lineMode",
  "lineEndpoint",

  // --- 铅笔笔触相关 ---
  // 标识未转图的矢量画笔组，刷新恢复后据此补转图
  "isPencilStroke",

  // --- 全能拆分图组标记 ---
  // 图组/子对象标记，刷新后保留「查看拆分详情」入口与图层文件匹配
  "_isSplitGroup",
  "_splitTaskId",
  "_splitLayerFile",
  "_splitLayerName",
  "_splitLayerId",
  // 拆分会话信息（图组）：刷新后从图组重建详情时恢复修补/回滚所需的 clientId/revision
  "_splitClientId",
  "_splitResultRevision",
  // 拆图结果最新 manifest URL（图组）：刷新后不依赖 RESUME 兜底即可定位最新清单（需求 59 补齐）
  "_splitResultManifestUrl",
  // 拆分会话所属作品 id（图组）：刷新后图组归属校验（跨画布复制/切换防护）
  "_splitArtworkId",
  // 拆分副本源身份（图组）：画廊「复制/定位」跨刷新判重，缺失则刷新后再点必重复复制
  "_splitSourceClientId",
  "_splitSourceVersionId",
  // 修补历史版本（图组子对象）：刷新后重建详情仍可「回滚修补」（需求 66 补齐）
  "_splitRepairVers",
  // 扫描态临时对象标记：刷新后残留副本据标签清理（断点续传恢复用）
  "_isSplitScanObject",

  // --- 扩展数据 ---
  "extraData",

  // --- 布局流相关 ---
  "isInFlow",
  "isReferencePoint",
  "isBottomGenerateCursor",
  "isPostProcessResult",
  "canvasInsertionOrder",

  // --- 基础状态 ---
  "locked",
  "visible",
  "selectable",
  "evented"
];

/**
 * 画布对象字段类型
 */
export type CanvasObjectField = keyof CanvasObjectProperties;

/**
 * 视口缩放限制
 */
export const VIEWPORT_ZOOM_LIMITS = {
  /** 最小缩放比例 (2%) */
  MIN_ZOOM: 0.02,
  /** 最大缩放比例 (300%) */
  MAX_ZOOM: 3
};

/**
 * 画布对象控制属性配置
 * 用于统一设置 Fabric.js 对象的交互控制属性
 */
export const CANVAS_OBJECT_CONTROL_CONFIG = {
  /** 正常图片/组合：可缩放、可旋转 */
  normal: {
    hasControls: true,
    hasBorders: true,
    hasRotatingPoint: true,
    lockRotation: false,
    lockScalingX: false,
    lockScalingY: false,
    lockUniScaling: false,
    uniformScaling: true,
    lockScalingFlip: true,
    perPixelTargetFind: false,
    transparentCorners: false,
    cornerSize: 8,
    cornerColor: "#8c5bec",
    borderColor: "#8c5bec",
    selectable: true,
    evented: true
  },
  /** 占位符：不可缩放、不可旋转，但显示边框 */
  placeholder: {
    hasControls: false,
    hasBorders: true,
    hasRotatingPoint: false,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    lockUniScaling: true,
    uniformScaling: true,
    lockScalingFlip: true,
    perPixelTargetFind: false,
    selectable: true,
    borderColor: "#8c5bec",
    // 完全隐藏控制点样式
    cornerSize: 0,
    transparentCorners: true
  }
} as const;

/**
 * 判断对象是否为占位符
 * @param obj 画布对象
 * @returns 是否为占位符
 */
export const isPlaceholderObject = (obj: any): boolean => {
  if (!obj?.isPlaceholder) return false;
  // 历史约定：仅 generation/upload 视为“任务占位符”
  // PSD 虽走 placeholderType 标识，但不纳入任务占位符语义（避免被历史快照过滤）
  return ["generation", "upload"].includes(String(obj?.placeholderType || ""));
};

/**
 * 判断对象是否为生图占位符
 * @param obj 画布对象
 * @returns 是否为生图占位符
 */
export const isGenerationPlaceholder = (obj: any): boolean => {
  return obj?.isPlaceholder === true && obj?.placeholderType === "generation";
};

/**
 * 判断对象是否为本地上传占位符
 * @param obj 画布对象
 * @returns 是否为本地上传占位符
 */
export const isUploadPlaceholder = (obj: any): boolean => {
  return obj?.isPlaceholder === true && obj?.placeholderType === "upload";
};

/**
 * 判断对象是否为 PSD 占位符
 * @param obj 画布对象
 * @returns 是否为 PSD 占位符
 */
export const isPsdPlaceholder = (obj: any): boolean => {
  return obj?.isPlaceholder === true && obj?.placeholderType === "psd";
};

/**
 * 判断占位符是否加载失败
 * @param obj 画布对象
 * @returns 是否加载失败
 */
export const isPlaceholderFailed = (obj: any): boolean => {
  return obj?.isPlaceholder === true && ["failed", "review_failed"].includes(obj?.placeholderStatus);
};
